import { Button } from "@/components/ui/button";
import { useRef } from "react";
import { useFormContext } from "react-hook-form";

type ProfilePictureStepProps = {
  step: number;
  setStep: (step: number) => void;
};

export default function ProfilePictureStep({
  step,
  setStep,
}: ProfilePictureStepProps) {
  const { register, setValue, watch } = useFormContext();
  const profileImage = watch("profileImage");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAvatarClick = () => {
    if (profileImage) {
      setValue("profileImage", undefined);
      setValue("avatarFile", undefined);
    }

    fileInputRef.current?.click();
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setValue("avatarFile", file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setValue("profileImage", reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleNext = () => {
    setStep(1);
  };

  const buttonClass =
    "w-full h-[48px] py-2 sm:py-3 px-4 sm:px-6 text-center font-semibold text-base sm:text-lg rounded-full";

  return (
    <>
      <div className="flex flex-col items-center justify-around">
        <h2 className="text-5xl font-bold mb-24 text-white text-center">
          UPLOAD PICTURE
        </h2>

        <div className="h-full">
          <div className="relative w-40 h-40 mb-36">
            <div
              className="w-40 h-40 rounded-full overflow-hidden flex items-center justify-center relative cursor-pointer"
              onClick={handleAvatarClick}
            >
              {profileImage ? (
                <img
                  src={profileImage}
                  alt="Profile"
                  className="w-full h-full object-cover"
                  title="Click to change image"
                />
              ) : (
                <img
                  src="/create-persona/avatar-placeholder.png"
                  alt="Profile Placeholder"
                  className="w-full h-full object-cover"
                  title="Click to upload image"
                />
              )}

              <div className="absolute inset-0 rounded-full border-4 border-white pointer-events-none z-10"></div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-md mx-auto w-full pb-12">
        {step === 0 && (
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <div className="w-full">
              <Button
                data-testid="profile-picture-upload-button"
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className={`${buttonClass} relative bg-transparent border border-[#c8ff00] text-[#c8ff00] flex items-center justify-center`}
              >
                {profileImage ? "CHANGE PICTURE" : "UPLOAD PICTURE"}
                <input
                  id="profile-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                  ref={fileInputRef}
                />

                <input {...register("avatarFile")} type="hidden" />
              </Button>
            </div>

            <div className="w-full">
              <Button
                onClick={handleNext}
                disabled={!profileImage}
                variant="default"
                className={buttonClass}
              >
                NEXT
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
